import { useState } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { DailyRevenueItem } from '../../types/pointhome.types';

type Props = { data: DailyRevenueItem[] };

const krw = (n: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(n);

const GRID5 = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 1.5,
} as const;

type Card = { key: string; label: string; value: number; to?: string };

function RevenueCard({ label, value, to }: { label: string; value: number; to?: string }) {
  const [imageError, setImageError] = useState(false);
  const serviceImagePath = `/img/publisher/${label}.png`;

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Box
      sx={{
        border: '1px solid #E0E3E7',
        borderRadius: 2,
        p: 2,
        bgcolor: '#fff',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: 120,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Avatar
          src={!imageError ? serviceImagePath : undefined}
          onError={handleImageError}
          sx={{
            width: 36,
            height: 36,
            bgcolor: '#EEF2F6',
            color: '#455A64',
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          {label[0]}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap title={label}>
            {label}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700}>
            {krw(value)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1.25, borderTop: '1px solid #ECEFF1' }} />

      <Box
        component={to ? RouterLink : 'div'}
        {...(to ? { to } : {})}
        sx={{
          mt: 1,
          color: 'text.secondary',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: to ? 'pointer' : 'default',
          width: 'fit-content',
          '&:hover': to ? { textDecoration: 'underline' } : undefined,
        }}
      >
        전체보기 <span aria-hidden>›</span>
      </Box>
    </Box>
  );
}

export default function PointhomeServiceRevenue({ data }: Props) {
  const sum = (fn: (d: DailyRevenueItem) => number) =>
    (data ?? []).reduce((acc, d) => acc + (fn(d) || 0), 0);


  const cards: ReadonlyArray<Card> = [
    { key: 'adpopcorn', label: '애드팝콘 SSP', value: sum((d) => d.SSP), to: '/pointhome/adpopcorn' },
    { key: 'buzzvil', label: '버즈빌', value: sum((d) => d.Buzzvil), to: '/pointhome/buzzvil' },
    { key: 'offerwall', label: '오퍼월', value: sum((d) => d.Offerwall), to: '/pointhome/offerwall' },
    { key: 'rc', label: 'RC', value: sum((d) => d.RC), to: '/pointhome/rc' },
    { key: 'cpc', label: 'CPC 광고', value: sum((d) => d.CPC) },
  ];

  return (
    <Box sx={{ mt: 2, overflowX: 'hidden' }}>
      <Box sx={GRID5}>
        {cards.map((c) => (
          <RevenueCard key={c.key} label={c.label} value={c.value} to={c.to} />
        ))}
        {Array.from({ length: (5 - (cards.length % 5 || 5)) % 5 }).map((_, i) => (
          <Box key={`spacer-${i}`} />
        ))}
      </Box>
    </Box>
  );
}
